import React from "react";
import { motion } from "framer-motion";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";
import { Badge } from "@/components/ui/badge";
import { BookOpen, Sparkles } from "lucide-react";

const stories = [
  {
    title: "Aarav and the Magic Mango Tree",
    description: "A boy discovers a talking mango tree in his grandma's garden",
    age: "Ages 3-6",
    gradient: "from-amber-400 to-orange-400",
    bgPattern: "bg-amber-50",
    textColor: "text-amber-900",
    illustration: "A lush garden with a magical glowing mango tree, the child standing in wonder",
  },
  {
    title: "Anaya's Diwali Adventure",
    description: "A girl saves Diwali when all the diyas go out",
    age: "Ages 4-8",
    gradient: "from-rose-400 to-pink-500",
    bgPattern: "bg-rose-50",
    textColor: "text-rose-900",
    illustration: "A girl lighting diyas with magical sparkles, a festive night sky",
  },
  {
    title: "The Little Astronaut from Bangalore",
    description: "A child builds a rocket from cardboard and reaches the moon",
    age: "Ages 5-10",
    gradient: "from-indigo-400 to-violet-500",
    bgPattern: "bg-indigo-50",
    textColor: "text-indigo-900",
    illustration: "A cardboard rocket soaring through stars, the child as astronaut",
  },
  {
    title: "Riya and the Friendly Tiger",
    description: "A jungle adventure with a twist ending",
    age: "Ages 4-8",
    gradient: "from-green-400 to-emerald-500",
    bgPattern: "bg-green-50",
    textColor: "text-green-900",
    illustration: "A child and tiger walking through a magical jungle trail",
  },
];

export default function StoryPreview() {
  return (
    <section id="story-preview" className="py-20 sm:py-28 bg-white relative overflow-hidden" data-testid="story-preview-section">
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-amber-100/40 rounded-full blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.6 }}
          className="text-center mb-14"
        >
          <span className="text-sm font-bold text-rose-500 uppercase tracking-widest mb-3 block">Story Collection</span>
          <h2 className="text-3xl sm:text-4xl font-bold text-indigo-950 tracking-tight mb-4" data-testid="story-preview-heading">
            Sneak Peek: See What Your Child's Book Looks Like
          </h2>
          <p className="text-base sm:text-lg text-indigo-950/50 max-w-lg mx-auto">
            Each story is beautifully illustrated with your child as the main character.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="px-0 sm:px-12"
        >
          <Carousel opts={{ align: "start", loop: true }} className="w-full" data-testid="story-carousel">
            <CarouselContent className="-ml-4">
              {stories.map((story, i) => (
                <CarouselItem key={i} className="pl-4 md:basis-1/2 lg:basis-1/2">
                  <div
                    className="h-full bg-white rounded-3xl border border-indigo-950/5 shadow-xl shadow-rose-900/5 overflow-hidden group hover:shadow-2xl transition-shadow duration-300"
                    data-testid={`story-card-${i + 1}`}
                  >
                    {/* Book cover mockup */}
                    <div className={`relative h-56 sm:h-64 ${story.bgPattern} overflow-hidden`}>
                      <div className={`absolute inset-0 bg-gradient-to-br ${story.gradient} opacity-20`} />
                      {/* Book illustration placeholder */}
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="text-center px-6">
                          <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${story.gradient} mx-auto mb-4 flex items-center justify-center shadow-lg opacity-80 group-hover:scale-110 transition-transform duration-500`}>
                            <BookOpen className="w-8 h-8 text-white" />
                          </div>
                          <p className={`text-xs ${story.textColor} opacity-60 italic`}>{story.illustration}</p>
                        </div>
                      </div>
                      {/* Blank face slot overlay on last card */}
                      {i === stories.length - 1 && (
                        <div className="absolute top-4 right-4 bg-white/90 backdrop-blur-sm rounded-xl px-3 py-2 shadow-md flex items-center gap-2">
                          <Sparkles className="w-4 h-4 text-amber-500" />
                          <span className="text-xs font-bold text-indigo-950">This could be YOUR child</span>
                        </div>
                      )}
                    </div>
                    {/* Story info */}
                    <div className="p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <Badge className="rounded-full bg-rose-50 text-rose-500 border-rose-100 text-xs font-semibold hover:bg-rose-50">
                          {story.age}
                        </Badge>
                      </div>
                      <h3 className="text-lg font-bold text-indigo-950 mb-2 group-hover:text-rose-500 transition-colors">
                        {story.title}
                      </h3>
                      <p className="text-sm text-indigo-950/50">{story.description}</p>
                    </div>
                  </div>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="hidden sm:flex -left-6 border-2 border-indigo-950/10 bg-white hover:bg-rose-50 hover:border-rose-200" data-testid="carousel-prev" />
            <CarouselNext className="hidden sm:flex -right-6 border-2 border-indigo-950/10 bg-white hover:bg-rose-50 hover:border-rose-200" data-testid="carousel-next" />
          </Carousel>
        </motion.div>
      </div>
    </section>
  );
}
